import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { doseWarnings, type DoseWarning } from "@/lib/doseSanity";
import { formatDose } from "@/lib/decimalInput";

export interface DoseConfirmLine {
  product_id?: string | null;
  product_name: string;
  rate_value: number;
  rate_unit: string;
}

/**
 * «¿Seguro que la dosis es correcta?» antes de guardar una receta o una tarea
 * con una dosis disparatada (A12 · T02-02). No bloquea: pregunta.
 *
 * Uso: `const { confirmDoses, doseConfirmDialog } = useDoseConfirm();` y, antes
 * de guardar, `if (!(await confirmDoses(lineas))) return;`. El diálogo se pinta
 * con `{doseConfirmDialog}`. La dosis habitual de cada producto se lee del
 * inventario en el momento; si esa lectura falla, se sigue mirando el tope.
 */
export function useDoseConfirm() {
  const { t, i18n } = useTranslation();
  const [pending, setPending] = useState<DoseWarning[] | null>(null);
  const resolver = useRef<((ok: boolean) => void) | null>(null);

  const confirmDoses = useCallback(async (lines: DoseConfirmLine[]): Promise<boolean> => {
    const ids = [...new Set(lines.map((l) => l.product_id).filter((id): id is string => !!id))];
    const defaults = new Map<string, { value: number | null; unit: string | null }>();
    if (ids.length > 0) {
      try {
        const { data } = await supabase
          .from("inventory")
          .select("id, default_dose_value, default_dose_unit, default_dose_is_legacy")
          .in("id", ids);
        for (const row of data || []) {
          // La dosis «heredada» no sabe en qué base está: no sirve para comparar.
          if (row.default_dose_is_legacy) continue;
          defaults.set(row.id, { value: row.default_dose_value, unit: row.default_dose_unit });
        }
      } catch {
        /* sin dosis habitual se mira solo el tope */
      }
    }
    const warnings = doseWarnings(
      lines.map((l) => ({
        product_name: l.product_name,
        rate_value: l.rate_value,
        rate_unit: l.rate_unit,
        default_dose_value: l.product_id ? defaults.get(l.product_id)?.value ?? null : null,
        default_dose_unit: l.product_id ? defaults.get(l.product_id)?.unit ?? null : null,
      })),
    );
    if (warnings.length === 0) return true;
    return new Promise<boolean>((resolve) => {
      resolver.current = resolve;
      setPending(warnings);
    });
  }, []);

  const close = (ok: boolean) => {
    const r = resolver.current;
    resolver.current = null;
    setPending(null);
    r?.(ok);
  };

  const lang = i18n?.language;
  const doseConfirmDialog = (
    <AlertDialog open={!!pending} onOpenChange={(o) => { if (!o) close(false); }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t("treatments.doseCheck.title")}</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-2 text-sm text-muted-foreground">
              <p>{t("treatments.doseCheck.description")}</p>
              <ul className="list-disc pl-5 space-y-1 text-foreground">
                {(pending || []).map((w) => (
                  <li key={`${w.product_name}-${w.rate_unit}`}>
                    {t(w.reason === "over_cap" ? "treatments.doseCheck.overCap" : "treatments.doseCheck.overDefault", {
                      product: w.product_name,
                      dose: `${formatDose(w.rate_value, lang)} ${w.rate_unit}`,
                      limit: `${formatDose(w.limit, lang)} ${w.limit_unit}`,
                    })}
                  </li>
                ))}
              </ul>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => close(false)}>{t("treatments.doseCheck.review")}</AlertDialogCancel>
          <AlertDialogAction onClick={() => close(true)}>{t("treatments.doseCheck.confirm")}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  return { confirmDoses, doseConfirmDialog };
}
